import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import { useCompany, useAuth } from '../../stores';
import AddCompanyButton from '../../components/addCompanyButton';
import ResellerSelect from '../../components/ResellerSelect';
import ActivePackagesSection from './invoicing/ActivePackagesSection';
import useCompanyId from '../../hooks/useCompanyId';
import { useSubscriptions } from '../../stores/FetcherStores/SubscriptionFetcher';
import { formatEndDate, timestampToDate } from '../../utils/date';
import CompanyEditDrawer from '../../components/CompanyEditDrawer';
import { useGetCompany } from '../../stores/FetcherStores/CompanyFetcher';

const Main = observer(() => {
  const { t } = useTranslation();
  const companyStore = useCompany();
  const companyId = useCompanyId();
  const auth = useAuth();
  const { data: company } = useGetCompany(companyId);
  const { data: subscriptions } = useSubscriptions(companyId);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  if (!companyId) {
    return null;
  }
  return (
    <Box>
      <Box
        sx={{
          marginTop: 2,
          marginBottom: 2,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h1" gutterBottom>
          {company?.name && company.name}
        </Typography>
        <Box sx={{ marginTop: 2, marginBottom: 2 }}>
          <AddCompanyButton companyKey={companyId} />
          <Button
            variant="contained"
            color="primary"
            sx={{ marginLeft: 2 }}
            onClick={() => setEditDrawerOpen(true)}
            endIcon={<EditIcon />}
          >
            Ändra
          </Button>
          {auth.isAdmin && (
            <Button
              variant="contained"
              color="secondary"
              className="btn btn-primary btn-lg"
              sx={{ marginLeft: 2 }}
              href={companyStore.firestoreLink()}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon />}
            >
              {t('companyScreen.openInFirebase')}
            </Button>
          )}
        </Box>
      </Box>
      <Paper>
        <Box sx={{ p: 2 }}>
          {auth.isAdmin && <Typography variant="body1">CompanyId: {companyId}</Typography>}
          <Typography variant="body1">
            {t('createCompanyScreen.orgNumber')}:{' '}
            {company?.organizationnumber && company.organizationnumber}
          </Typography>
          <Typography variant="body1">
            {t('createCompanyScreen.address')}: {company?.street && company.street}
          </Typography>
          <Typography variant="body1">
            {t('createCompanyScreen.zipCode')}: {company?.postalcode && company.postalcode}
          </Typography>
          <Typography variant="body1">
            {t('createCompanyScreen.municipality')}: {company?.city && company.city}
          </Typography>
          <Typography variant="body1">
            {t('createCompanyScreen.email')}: {company?.email && company.email}
          </Typography>
          <Typography variant="body1">
            {t('createCompanyScreen.ownerEmail')}: {company?.ownerEmail && company.ownerEmail}
          </Typography>
          <Typography variant="body1">
            {t('createCompanyScreen.phoneNumber')}: {company?.phone && company.phone}
          </Typography>
          <Typography variant="body1">
            {t('companyScreen.appVersionNumber')}:{' '}
            {company?.appVersionNumber && company.appVersionNumber}
          </Typography>
          {auth.isAdmin && (
            <>
              <Typography variant="body1">InvoiceMode: {company?.invoicingMode}</Typography>
              {company?.invoicingMode === 'INVOICE' && (
                <Typography variant="body1">
                  Fortnox customer ID: {company?.customerId && company.customerId}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Paper>

      {auth.isAdmin && <ResellerSelect />}

      <ActivePackagesSection />

      <Paper sx={{ mt: 2, p: 2 }}>
        <Typography variant="h2">Prenumerationer</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Prenumeration ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Slutdatum</TableCell>
                <TableCell>Stripe Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions?.map((subscription) => (
                <TableRow key={subscription.id}>
                  <TableCell>{subscription.id}</TableCell>
                  <TableCell>{subscription.data.status}</TableCell>
                  <TableCell>{formatEndDate(timestampToDate(subscription.data.endDate))}</TableCell>
                  <TableCell>{subscription.data.stripeStatus}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CompanyEditDrawer open={editDrawerOpen} onClose={() => setEditDrawerOpen(false)} />
    </Box>
  );
});

export default Main;
